import moment from 'moment'
import { z } from 'zod'
import { decodeJwtPayload } from 'auth/jwt'

/**
 * How long the provider rings when the server's ring window cannot be measured at all (QMD-4640).
 *
 * This is a last resort, not the normal path: `classifyRingWindow` measures the real window from
 * two server timestamps and honours it exactly. We only land here when the Daily access token
 * cannot be read, which should effectively never happen.
 *
 * 70s, because the server's window is 60s (see `readServerRingWindowMs`) and the expiry exists to
 * keep the provider's ring in step with patient-mobile's. Mobile runs its own copy of this
 * arithmetic against a phone clock that is almost always correct. Erring long leaves the provider
 * ringing ~10s after mobile gave up (harmless). Erring short hangs up on a patient who is still
 * being rung, which is the drift bug the shared expiry was introduced to kill.
 *
 * Residual risk, bounded and deliberate: if the token becomes unreadable AND the backend has
 * meanwhile lengthened its window past 70s, this rings short. That needs both to be true at once.
 * Raise this constant if the server window ever moves.
 */
export const SKEW_FALLBACK_RING_WINDOW_MS = 70_000

/**
 * Sanity bounds on the window we read off the server's own timestamps.
 *
 * NOT a skew detector. The measurement below never touches a client clock, so skew cannot reach
 * it; these only reject a window that is not a credible ring duration at all (a malformed `iat`,
 * a token from an unrelated system). They are therefore wide, and a legitimate change to the
 * server's window passes straight through and is honoured exactly rather than being clamped.
 */
export const MIN_SANE_SERVER_WINDOW_MS = 10_000
export const MAX_SANE_SERVER_WINDOW_MS = 300_000

/**
 * The claims we need off the Daily access token. `iat` is seconds since epoch, stamped by Daily
 * when the backend asks it to mint the meeting token for this call, so it is a SERVER clock
 * reading of the moment the expiry was issued. Daily documents it and includes it in their own
 * example payload; `.optional()` because it is their field, not a contract we control, and the
 * fallback below is what makes that acceptable.
 */
const DailyTokenSchema = z.object({ iat: z.number().optional() })

export type RingWindowVerdict =
  /** The normal path: window measured from two server timestamps, honoured exactly. */
  | 'server-issued'
  /** No JWT, or no `iat` claim on it. Nothing to measure against. */
  | 'token-unreadable'
  /** `iat` read, but the window it implies is not a credible ring duration. */
  | 'implausible-server-window'
  /** `ExpireDateTimeUtc` could not be parsed. */
  | 'unparseable-expiry'

export type RingWindow = {
  verdict: RingWindowVerdict
  /** How long the provider will ring, in ms. */
  windowMs: number
  /** The window the server's own timestamps imply, in ms. `null` when it could not be measured. */
  issuedWindowMs: number | null
  /** The client clock reading `windowMs` is counted from. */
  startedAtMs: number
}

type ClassifyInput = {
  /** `ExpireDateTimeUtc` exactly as the server sent it. */
  expiresAtIso: string
  /** `AccessToken` from the same response: the Daily meeting token, carrying the server's `iat`. */
  accessToken: string | null
  /** Client clock read the moment the response arrived. The countdown anchor. */
  receivedAtMs: number | null
  /** Current client clock reading. Only used when there is no receipt time to anchor to. */
  nowMs: number
}

/**
 * The server's ring window, in ms, measured WITHOUT any client clock (QMD-4640).
 *
 * `ExpireDateTimeUtc` and the token's `iat` are both server-side readings taken at the same
 * instant, so their difference is the real window `R` and the provider's broken clock cannot
 * contaminate it. Measured at 60.085s on QA 2026-10-06: `ExpireDateTimeUtc` 14:37:58.085Z minus
 * the token's `iat` 14:36:58Z. The 85ms residual is the offset between Daily's clock and the
 * backend's plus round-trip, and is itself evidence the two server clocks agree.
 *
 * This is what the earlier design could not do. Diffing the server's expiry against the CLIENT's
 * receipt time yields `R - skew - latency`: one equation, two unknowns, which is why that version
 * needed a plausibility band to guess which was which, and still read a clock up to 40s fast as
 * healthy. Here `R` is known, and skew is handled separately by `remainingRingMs`, which compares
 * two client readings so it cancels exactly. Two clean equations instead of one muddled one.
 *
 * Returns `null` when the token yields nothing usable, including a signature-free read of a token
 * we never verify: we are deriving a duration, not making a trust decision, and a forged value is
 * caught by the sanity bounds in `classifyRingWindow`.
 */
function readServerRingWindowMs(accessToken: string | null, expiresAtMs: number): number | null {
  if (!accessToken) return null
  const parsed = DailyTokenSchema.safeParse(decodeJwtPayload(accessToken))
  if (!parsed.success || parsed.data.iat === undefined) return null
  return expiresAtMs - parsed.data.iat * 1_000
}

/**
 * Decides how long to ring, without trusting the browser's clock (QMD-4640).
 *
 * The verdict is fixed the moment the response lands and never changes, so re-running this later
 * can never reach a different answer. The caller then measures elapsed time with
 * `remainingRingMs`, a client-to-client delta in which skew cancels.
 *
 * `ExpireDateTimeUtc` is parsed with `moment` rather than `Date.parse`, which is
 * implementation-defined for the non-strict ISO forms the backend could emit and would silently
 * yield `NaN` or a wrong instant. (Confirmed carrying an explicit `+00:00` on QA 2026-10-06, so
 * it is read as UTC and not as local time.)
 */
export function classifyRingWindow({ expiresAtIso, accessToken, receivedAtMs, nowMs }: ClassifyInput): RingWindow {
  // `receivedAtMs` is typed `number | null` but reaches the store through an untyped legacy
  // `action.extraData`, so a dispatcher that omits it writes `undefined` with `tsc` silent. An
  // unchecked non-finite value would make `remainingRingMs` return NaN, and `NaN > 0` is false,
  // which is an instant "No answer" — QMD-4640 itself. Anchor to now instead.
  const startedAtMs = receivedAtMs !== null && Number.isFinite(receivedAtMs) ? receivedAtMs : nowMs
  const fallback = (verdict: RingWindowVerdict, issuedWindowMs: number | null = null): RingWindow => ({
    verdict,
    windowMs: SKEW_FALLBACK_RING_WINDOW_MS,
    issuedWindowMs,
    startedAtMs,
  })

  const parsed = moment(expiresAtIso)
  if (!parsed.isValid()) return fallback('unparseable-expiry')

  const issuedWindowMs = readServerRingWindowMs(accessToken, parsed.valueOf())
  if (issuedWindowMs === null) return fallback('token-unreadable')

  if (issuedWindowMs < MIN_SANE_SERVER_WINDOW_MS || issuedWindowMs > MAX_SANE_SERVER_WINDOW_MS) {
    return fallback('implausible-server-window', issuedWindowMs)
  }

  return { verdict: 'server-issued', windowMs: issuedWindowMs, issuedWindowMs, startedAtMs }
}

/** Milliseconds until the provider should give up ringing. `<= 0` means the window has elapsed. */
export function remainingRingMs(ringWindow: RingWindow, nowMs: number): number {
  return ringWindow.windowMs - (nowMs - ringWindow.startedAtMs)
}
